import React, { useEffect, useState } from 'react';
import { FileText, RefreshCw } from 'lucide-react';
import { backofficeAuth } from '../backofficeFirebaseClient';

const DEFAULT_FUNCTIONS_BASE = 'https://us-central1-acctoo-backoffice.cloudfunctions.net';

const AdminAuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const token = await backofficeAuth.currentUser?.getIdToken?.();
      if (!token) throw new Error('Not signed in');
      const base = process.env.REACT_APP_BACKOFFICE_FUNCTIONS_URL || DEFAULT_FUNCTIONS_BASE;
      const r = await fetch(`${base}/api/v1/admin/audit-logs?limit=200`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || j.message || r.statusText);
      setLogs(Array.isArray(j.logs) ? j.logs : []);
      setLastSyncedAt(new Date().toISOString());
    } catch (e) {
      setError(e.message || 'Failed to load audit logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const formatTs = (ts) => {
    if (!ts) return '—';
    try {
      if (ts.toDate) return ts.toDate().toLocaleString();
      if (ts._seconds) return new Date(ts._seconds * 1000).toLocaleString();
      return String(ts);
    } catch (_) {
      return '—';
    }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-indigo-600" />
          <h2 className="text-xl font-semibold">Admin audit log</h2>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
      {lastSyncedAt && (
        <p className="text-xs text-gray-500">
          Last synced: {lastSyncedAt} (source: manual_refresh)
        </p>
      )}
      {error && <div className="text-sm text-red-600">{error}</div>}
      <div className="bg-white border rounded-lg overflow-auto max-h-[70vh]">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="p-2 border-b">Time</th>
              <th className="p-2 border-b">Action</th>
              <th className="p-2 border-b">Company</th>
              <th className="p-2 border-b">Actor</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((row) => (
              <tr key={row.id} className="border-b last:border-0">
                <td className="p-2 whitespace-nowrap">{formatTs(row.ts)}</td>
                <td className="p-2">{row.action || '—'}</td>
                <td className="p-2 font-mono text-xs">{row.companyId || '—'}</td>
                <td className="p-2 text-xs">{row.actor?.email || row.actor?.uid || '—'}</td>
              </tr>
            ))}
            {!loading && logs.length === 0 && (
              <tr>
                <td colSpan={4} className="p-4 text-gray-500">
                  No audit entries yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminAuditLogs;
